import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authFetch } from "../services/authAPI";
import moment from "moment";
import "moment/locale/fr";
import Modal from "../components/Modal";

const ROLE_LABELS = {
    ROLE_ADMIN: "Administrateur",
    ROLE_MANAGER: "Manager",
    ROLE_FLEET_MANAGER: "Gestionnaire de flotte",
    ROLE_EMPLOYEE: "Employé"
};

const ROLE_CLASSES = {
    ROLE_ADMIN: "primary",
    ROLE_MANAGER: "success",
    ROLE_FLEET_MANAGER: "info",
    ROLE_EMPLOYEE: "secondary"
};

export default function UserPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showDeleteModal, setShowDeleteModal] = useState(false);

    async function fetchUser() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/users/${id}`);
            if (!response.ok) {
                setError("Utilisateur introuvable.");
                return;
            }
            const data = await response.json();
            setUser(data);
        } catch (err) {
            setError("Erreur lors du chargement de l'utilisateur.");
            // silently fail
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchUser();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    async function handleDelete() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/users/${id}`, {
                method: "DELETE",
            });
            if (response.ok) {
                setShowDeleteModal(false);
                fetchUser();
            }
        } catch (err) {
            // silently fail
        }
    }

    async function handleRestore() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/users/${id}/restore`, {
                method: "POST",
            });
            if (response.ok) {
                fetchUser();
            }
        } catch (err) {
            // silently fail
        }
    }

    if (loading) {
        return (
            <section>
                <div className="card mb-4">
                    <div className="card-body py-5 px-5 text-center">
                        <p className="text-muted">Chargement...</p>
                    </div>
                </div>
            </section>
        );
    }

    if (error || !user) {
        return (
            <section>
                <div className="card mb-4">
                    <div className="card-body py-5 px-5 text-center">
                        <p className="text-danger">{error ?? "Utilisateur introuvable."}</p>
                        <button className="btn btn-link" onClick={() => navigate("/users")}>
                            Retour aux utilisateurs
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <>
            <Modal
                title="Supprimer l'utilisateur"
                body={
                    <p>
                        Êtes-vous sûr de vouloir supprimer l'utilisateur{" "}
                        <strong>{user.firstName} {user.lastName}</strong> ?
                    </p>
                }
                button={{ close: "Annuler", valid: "Supprimer" }}
                showModal={showDeleteModal}
                setShowModal={setShowDeleteModal}
                onSubmit={handleDelete}
            />
            <section>
                <div className="card mb-4">
                    <div className="card-header py-3">
                        <div className="row py-3 align-items-center">
                            <div className="col-sm-6 text-center text-sm-start">
                                <button
                                    type="button"
                                    className="btn btn-light me-2"
                                    onClick={() => navigate(-1)}
                                >
                                    <i className="fas fa-arrow-left me-2"></i>
                                    Retour
                                </button>
                            </div>
                            <div className="col-sm-6 text-center text-sm-end">
                                {user.deletedAt ? (
                                    <button
                                        type="button"
                                        className="btn btn-success me-2"
                                        onClick={handleRestore}
                                    >
                                        Restaurer
                                        <i className="fas fa-rotate-left ms-2"></i>
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn btn-danger me-2"
                                        onClick={() => setShowDeleteModal(true)}
                                    >
                                        Supprimer
                                        <i className="fas fa-trash ms-2"></i>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="card-body py-5 px-5">
                        <div className="row g-4">
                            <div className="col-md-6">
                                <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Prénom</h6>
                                <p className="fw-bold mb-0">{user.firstName}</p>
                            </div>
                            <div className="col-md-6">
                                <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Nom</h6>
                                <p className="fw-bold mb-0">{user.lastName}</p>
                            </div>
                            <div className="col-md-6">
                                <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Email</h6>
                                <p className="mb-0">{user.email}</p>
                            </div>
                            <div className="col-md-6">
                                <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Rôle</h6>
                                <p className="mb-0">
                                    <span className={"badge badge-" + (ROLE_CLASSES[user.role] ?? "secondary")}>
                                        {ROLE_LABELS[user.role] ?? user.role}
                                    </span>
                                </p>
                            </div>
                            <div className="col-md-6">
                                <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Statut</h6>
                                <p className="mb-0">
                                    {user.deletedAt ? (
                                        <>
                                            <i className="fa-solid fa-circle-xmark text-danger me-2"></i>
                                            <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                                                Supprimé le {moment(user.deletedAt).locale("fr").format("dddd Do MMMM YYYY [à] HH:mm")}
                                            </span>
                                        </>
                                    ) : (
                                        <i className="fa-solid fa-circle-check text-success"></i>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
